import { ColumnType } from 'antd/es/table'
import SourceFile from '../components/cells/source-file/source-file'
import CompleteTask from '../components/complete-task'
import { DesignerTasksTableRow } from '../types/table-row'
import StampDetails from '../components/stamp-details/stamp-details'
import { isGroupHeader, isNoShipmentHeader, isShipmentHeader } from '../helpers/row-helpers'
import {
    HEADER_CONTENT_COL_SPAN,
    onHeaderHiddenCell,
    renderTaskOnly,
} from '../helpers/table-helpers'
import ShipmentHeadingCell from '../components/shipment-heading-cell/shipment-heading-cell'
import renderDatetime from '../../../helpers/tables/render-datetime'
import TaskStatusCell from '../components/cells/task-status-cell/task-status-cell'
import { BLANK_TYPE_BUTTON_COLOR } from '../../../types/api/shipment-blank-type'
import PreviewShipmentBlank from '../../../components/buttons/preview-shipment-blank/preview-shipment-blank'
import BlankPreviewFile from '../components/cells/blank-preview-file/blank-preview-file'
import { Badge, Button, Space, Tooltip, Typography } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import {
    formatFormReorderDetailsLabel,
    getFormOrderDetailsFields,
} from '../helpers/form-order-details'

export const getColumns = (newStatusCount: number): ColumnType<DesignerTasksTableRow>[] => [
    {
        dataIndex: ['id'],
        title: 'Номер',
        width: 60,
        onCell: onHeaderHiddenCell,
        render: renderTaskOnly(record => record.id),
    },
    {
        dataIndex: ['type'],
        width: 120,
        title: 'Тип задачи',
        onCell: onHeaderHiddenCell,
        render: renderTaskOnly(record => record.type),
    },
    {
        title: 'Детали',
        onCell: record =>
            isGroupHeader(record) ? { colSpan: HEADER_CONTENT_COL_SPAN } : {},
        render: (_v, record) => {
            if (isNoShipmentHeader(record)) {
                return (
                    <Typography.Text style={{ fontSize: 16, fontWeight: 'bold' }}>
                        Без отгрузки
                    </Typography.Text>
                )
            }

            if (isShipmentHeader(record)) {
                return <ShipmentHeadingCell shipment={record} />
            }

            switch (record.type) {
                case 'Отрисовка штампа':
                    return record.shipment?.stamp ? (
                        <StampDetails stamp={record.shipment.stamp} />
                    ) : (
                        ''
                    )
                case 'Отрисовка бланка':
                    return record.description || ''
                case 'Заказ форм':
                    if (record.shipment?.numberOfForms && record.shipment?.stamp) {
                        return (
                            <>
                                Количество форм: {record.shipment.numberOfForms}
                                <br />
                                <StampDetails stamp={record.shipment.stamp} />
                            </>
                        )
                    }
                    return ''
                case 'Перезаказ форм': {
                    const details = getFormOrderDetailsFields(record)
                    return (
                        <>
                            {formatFormReorderDetailsLabel(details)}
                            {record.shipment?.stamp ? (
                                <>
                                    <br />
                                    <StampDetails stamp={record.shipment.stamp} />
                                </>
                            ) : null}
                        </>
                    )
                }
                default:
                    return ''
            }
        },
    },
    {
        dataIndex: ['taskStatus'],
        width: 110,
        title: (
            <Space size={4}>
                Статус
                {newStatusCount > 0 && (
                    <Tooltip title={'Количество ваших задач в статусе «Новая»'}>
                        <Badge count={newStatusCount} overflowCount={99} />
                    </Tooltip>
                )}
            </Space>
        ),
        onCell: onHeaderHiddenCell,
        render: renderTaskOnly(record => <TaskStatusCell taskStatus={record.taskStatus} />),
    },
    {
        dataIndex: ['createdAt'],
        width: 110,
        title: 'Создана',
        onCell: onHeaderHiddenCell,
        render: renderTaskOnly(record => renderDatetime(record.createdAt)),
    },
    {
        dataIndex: ['sourceFile'],
        width: 110,
        title: 'Исходники',
        onCell: onHeaderHiddenCell,
        render: renderTaskOnly(record => <SourceFile task={record} />),
    },
    {
        dataIndex: 'blank',
        width: 180,
        title: 'Бланки',
        onCell: onHeaderHiddenCell,
        render: renderTaskOnly(record => {
            const shipment = record.shipment
            if (!shipment) return '-'

            const hasPreview = Boolean(shipment.blankPreviewFiles?.length)

            return (
                <Space size='small'>
                    <PreviewShipmentBlank
                        shipment={shipment}
                        blankType='client'
                        blankPreviewFiles={shipment.blankPreviewFiles}
                    >
                        <Button
                            title='Посмотреть клиентский бланк'
                            size='small'
                            icon={<EyeOutlined />}
                            color={BLANK_TYPE_BUTTON_COLOR.client}
                            variant='solid'
                            disabled={!hasPreview}
                        />
                    </PreviewShipmentBlank>
                    <PreviewShipmentBlank
                        shipment={shipment}
                        blankType='production'
                        blankPreviewFiles={shipment.blankPreviewFiles}
                    >
                        <Button
                            title='Посмотреть произв. бланк'
                            size='small'
                            icon={<EyeOutlined />}
                            color={BLANK_TYPE_BUTTON_COLOR.production}
                            variant='solid'
                            disabled={!hasPreview}
                        />
                    </PreviewShipmentBlank>
                    <BlankPreviewFile task={record} />
                </Space>
            )
        }),
    },
    {
        dataIndex: 'actions',
        title: 'Действия',
        fixed: 'right',
        width: 80,
        render: renderTaskOnly(record => <CompleteTask task={record} />),
    },
]
